/**
 * Privacy Policy Page
 * Clean, minimal design using on record Color Bible
 */

import LegalPageLayout from '../components/LegalPageLayout'

export default function PrivacyPolicy() {
  return (
    <LegalPageLayout title="Privacy Policy" lastUpdated="December 2025">
      <div className="space-y-12" style={{ color: '#111111', lineHeight: '1.75' }}>
        {/* Introduction */}
        <section>
          <p className="text-lg">
            At on record, we take your privacy seriously. This Privacy Policy explains how we collect, use, store,
            and protect your information when you use our platform. We are committed to transparency and giving
            you control over your data.
          </p>
        </section>

        {/* 1. Information We Collect */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            1. Information We Collect
          </h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#0A2342' }}>
                1.1 Information You Provide
              </h3>
              <p className="mb-3">
                When you create an account and use on record, you may provide us with:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
                <li>Account information such as name, username, email address, and profile details</li>
                <li>Content you create, post, or share including text, images, videos, and comments</li>
                <li>Communications with us or other users through the platform</li>
                <li>Payment information if you use paid features or services</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#0A2342' }}>
                1.2 Automatically Collected Information
              </h3>
              <p className="mb-3">
                When you use on record, we automatically collect certain information:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
                <li>Device information including device type, operating system, and unique identifiers</li>
                <li>Usage information such as features used, content viewed, and interactions</li>
                <li>Technical data including IP address, browser type, and access times</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#0A2342' }}>
                1.3 Privacy-Enhanced Location Information
              </h3>
              <p className="mb-3">
                on record collects location information in a privacy-enhanced manner:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
                <li>We collect only generic, city-level location information — no exact coordinates</li>
                <li>Location data is stored separately from user identity to prevent individual tracking</li>
                <li>Posts are tagged with city/country only for authenticity verification</li>
                <li>Like analytics show aggregated city data with no personal identification</li>
                <li>You can control location permissions through your device settings</li>
              </ul>
            </div>
          </div>
        </section>

        {/* 2. How We Use Your Information */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            2. How We Use Your Information
          </h2>
          <p className="mb-4">We use the information we collect to:</p>
          <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
            <li>Provide, maintain, and improve on record's services and features</li>
            <li>Verify the authenticity of content through our on record Code system</li>
            <li>Enable privacy-safe engagement metrics through city-level analytics</li>
            <li>Communicate with you about updates, features, and support</li>
            <li>Protect against fraud, spam, and abuse</li>
            <li>Comply with legal obligations</li>
          </ul>

          <h3 className="text-xl font-bold mt-8 mb-4" style={{ color: '#0A2342' }}>2.1 Making on record Smarter Together</h3>
          <p className="mb-4">When you share public posts on on record, you're helping build a smarter platform for everyone. We use public post content and AI-generated descriptions to improve our technology — specifically to:</p>
          <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
            <li>Get better at detecting fake, manipulated, and AI-generated content</li>
            <li>Improve the accuracy of authenticity verification</li>
            <li>Make content categorization and scene understanding more reliable</li>
            <li>Strengthen our ability to identify misinformation</li>
          </ul>
          <p className="mt-4 mb-2"><strong>Only public content is used</strong> — the text, images, and metadata you choose to share publicly, along with any AI-generated descriptions. Your personal account details, private posts, private reports to authorities, and location history are never part of this.</p>
          <p className="mb-4"><strong>You're always in control.</strong> If you'd prefer your content not to be used for these improvements, simply set your posts to private. Public posts help the platform learn; private posts remain entirely yours.</p>
        </section>

        {/* 3. on record Code System */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            3. on record Code System
          </h2>
          <p className="mb-4">
            Each post on on record receives a unique 12-digit on record Code. This system is designed with privacy in mind:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
            <li>Codes serve as permanent, shareable references for content verification</li>
            <li>No personal data is embedded in on record Codes</li>
            <li>Codes enable authentication without exposing post IDs or user information</li>
            <li>The system facilitates content tracking while preserving user privacy</li>
          </ul>
        </section>

        {/* 4. Reporter Mode - Privacy-First Design */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            4. Reporter Mode Privacy Protection
          </h2>
          <p className="mb-4">
            on record's Reporter Mode is built with privacy at its core. When you enable Reporter Mode to contribute
            content, the following protections are in place:
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#0A2342' }}>
                4.1 Anonymous Session Identifiers
              </h3>
              <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
                <li>You are assigned a random session ID (format: RPT-XXXXXXX) that is not linked to your account publicly</li>
                <li>This ID is randomly generated and changes each session</li>
                <li>Your session ID automatically rotates every 2 hours, even while Reporter Mode stays active</li>
                <li>When you disable Reporter Mode, your session ID is immediately invalidated</li>
                <li>There is no way to correlate the same user across different time periods</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#0A2342' }}>
                4.2 Device Information Protection
              </h3>
              <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
                <li>We categorize your device into quality tiers (premium, high, standard, basic) rather than storing exact device models</li>
                <li>Camera capabilities are summarized (e.g., "4K capable", "HD capable") without revealing specific hardware</li>
                <li>No unique device identifiers are exposed to administrators</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#0A2342' }}>
                4.3 Location Privacy
              </h3>
              <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
                <li>Location is stored at reduced precision (area-level, not exact coordinates)</li>
                <li>Only approximate area names are visible, not precise addresses</li>
                <li>Administrators see general areas, not your exact position</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#0A2342' }}>
                4.4 Task Assignment Privacy
              </h3>
              <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
                <li>Tasks are broadcast to areas, not assigned to specific individuals</li>
                <li>Administrators cannot target or identify specific reporters</li>
                <li>Administrators can only see "available reporters in an area" without knowing who they are</li>
                <li>You choose which tasks to accept - there is no forced assignment</li>
              </ul>
            </div>
          </div>
        </section>

        {/* 5. Content Deletion and Data Retention */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            5. Content Deletion and Data Retention
          </h2>
          <p className="mb-4">
            on record is designed as an evidence-first platform for authentic news and media. Our deletion and retention
            policies reflect this mission while balancing user privacy rights:
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#0A2342' }}>
                5.1 Private Media (Not Yet Posted)
              </h3>
              <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
                <li>You have full deletion rights for media stored in your private folders</li>
                <li>Deleted private media is permanently removed from our servers</li>
                <li>No backups or copies are retained after deletion</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#0A2342' }}>
                5.2 Public Posts (Evidence Preservation)
              </h3>
              <p className="mb-3">
                Once media is posted publicly on on record, it enters our Evidence Preservation system:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
                <li><strong>Soft Deletion:</strong> When you delete a post, it is hidden from public view immediately</li>
                <li><strong>Media Preservation:</strong> Original media files may be retained for up to 7 years for legal compliance and law enforcement purposes</li>
                <li><strong>Thumbnail Retention:</strong> Thumbnails and basic metadata (including on record Code) are permanently preserved</li>
                <li><strong>Audit Trail:</strong> All deletion actions are logged for compliance purposes</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#0A2342' }}>
                5.3 Why We Preserve Public Content
              </h3>
              <p className="mb-3">
                on record's mission is to provide authentic, verifiable news and evidence. We preserve public content because:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
                <li>Posted content may be cited as evidence in legal, journalistic, or historical contexts</li>
                <li>on record Codes provide permanent verification links for cited content</li>
                <li>Law enforcement agencies may require access to content for investigations</li>
                <li>Maintaining content integrity prevents manipulation or falsification of records</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#0A2342' }}>
                5.4 Shared Media
              </h3>
              <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
                <li>If you share media with other users, it may be preserved for their continued access</li>
                <li>Reference counting ensures shared media is not deleted while others have access</li>
                <li>You can revoke sharing access, but this does not delete media from others' posts</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#0A2342' }}>
                5.5 Permanent Deletion (Admin Only)
              </h3>
              <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
                <li>Complete and permanent deletion of public content is only available to administrators</li>
                <li>Permanent deletion may be requested for legal reasons (court order, DMCA, etc.)</li>
                <li>All admin deletion actions are logged with reason codes for compliance</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#0A2342' }}>
                5.6 Data Retention Periods
              </h3>
              <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
                <li><strong>Private media:</strong> Deleted immediately upon user request</li>
                <li><strong>Public post visibility:</strong> Hidden immediately upon deletion</li>
                <li><strong>Media files:</strong> Retained for up to 7 years (law enforcement compliance)</li>
                <li><strong>Thumbnails and metadata:</strong> Retained permanently</li>
                <li><strong>on record Codes:</strong> Retained permanently as verification records</li>
                <li><strong>Account data:</strong> Deleted within 30 days of account deletion request</li>
              </ul>
            </div>
          </div>
        </section>

        {/* 6. Data Sharing */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            6. Data Sharing and Disclosure
          </h2>
          <p className="mb-4">We do not sell your personal information. We may share your information only in these limited circumstances:</p>
          <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
            <li>With your consent or at your direction</li>
            <li>With service providers who help us operate on record</li>
            <li>To comply with legal obligations or valid legal requests</li>
            <li>To protect the rights, safety, and security of on record and our users</li>
          </ul>
        </section>

        {/* 7. Your Rights */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            7. Your Privacy Rights
          </h2>
          <p className="mb-4">You have the following rights regarding your data:</p>
          <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
            <li><strong>Access:</strong> Request a copy of your personal information</li>
            <li><strong>Correction:</strong> Update or correct inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your account and data</li>
            <li><strong>Export:</strong> Download your data in a portable format</li>
            <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
          </ul>
          <p className="mt-4">
            To exercise these rights, contact us at{' '}
            <a href="mailto:privacy@onrecord.com" style={{ color: '#FFD43B' }} className="hover:underline">
              privacy@onrecord.com
            </a>
          </p>
        </section>

        {/* 8. Data Security */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            8. Data Security
          </h2>
          <p>
            We implement industry-standard security measures to protect your information from unauthorized access,
            disclosure, alteration, or destruction. However, no method of transmission over the internet is 100% secure,
            and we cannot guarantee absolute security.
          </p>
        </section>

        {/* 9. International Data Transfers */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            9. International Data Transfers
          </h2>
          <p>
            Your information may be transferred to and processed in countries other than your country of residence.
            We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
          </p>
        </section>

        {/* 10. Children's Privacy */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            10. Children's Privacy
          </h2>
          <p>
            on record is intended for users aged 13 and older. We do not knowingly collect personal information from
            children under 13. If you believe we have collected information from a child under 13, please contact us
            immediately.
          </p>
        </section>

        {/* 11. Changes to This Policy */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            11. Changes to This Policy
          </h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any material changes by posting
            the new Privacy Policy on this page and updating the "Effective" date at the top.
          </p>
        </section>

        {/* 12. Contact Us */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            12. Contact Us
          </h2>
          <p className="mb-4">
            If you have questions about this Privacy Policy or our privacy practices, please contact us:
          </p>
          <div style={{ color: '#6B7280' }}>
            <p>Email: <a href="mailto:contact@onrecord.live" style={{ color: '#FFD43B' }} className="hover:underline">contact@onrecord.live</a></p>
          </div>
        </section>
      </div>
    </LegalPageLayout>
  )
}
